import { Autocomplete, TextField, TextFieldProps } from '@mui/material';
import React, { useEffect, useState, useCallback, forwardRef } from 'react';
import { useIntl } from 'react-intl';
import qs from 'qs';
import { debounce, get, isEmpty } from 'lodash';

import { currentLocale as lang } from '../../I18n';

interface StructuredFormatting {
  bbox: Array<object>;
  geometry: object;
  properties: object;
  type: string;
}

const fetchSuggestions = async (query: any, extraQueryParams: any, callback: any) => {
  const url = `https://api.geoapify.com/v1/geocode/autocomplete?${qs.stringify({
    text: query.trim(),
    ...extraQueryParams,
    lang,
    apiKey: '86b9d1f6041546578b8575211d884ce1',
  })}`;
  const response = await fetch(url);
  const results = await response.json();

  callback(results.features);
};

type AddressProps = TextFieldProps & {
  extraQueryParams?: object;
  inputValue?: string;
  setError?: any;
  clearErrors?: any;
  freeSolo?: boolean;
  required?: boolean;
  geolocated?: boolean;
  setInputValue?: any;
  setAddress?: any;
  onClear?: any;
  name: string;
};

const Address = forwardRef(
  (
    {
      label,
      placeholder = '',
      value,
      extraQueryParams,
      inputValue,
      setError,
      clearErrors,
      freeSolo = true,
      required,
      geolocated = false,
      setInputValue = () => {},
      setAddress = () => {},
      onClear = () => {},
      name,
      ...props
    }: AddressProps,
    ref,
  ) => {
    const intl = useIntl();
    const [options, setOptions] = useState([]);

    const debouncedSearch = useCallback(
      debounce((text) => {
        if (text) {
          fetchSuggestions(text, extraQueryParams, setOptions);
        }
      }, 500),
      [],
    );

    useEffect(() => {
      if (isEmpty(inputValue)) {
        return undefined;
      }
      debouncedSearch(inputValue);

      return debouncedSearch.cancel;
    }, [inputValue, debouncedSearch]);

    return (
      <Autocomplete
        value={value || null}
        isOptionEqualToValue={(option: any, val: any) => {
          if (freeSolo) {
            return get(option, 'properties.formatted') === val || option === val;
          }
          return true;
        }}
        filterOptions={(o: any) => o} // Show all the suggestions and skip autocomplete own name matching
        freeSolo={freeSolo}
        options={options}
        disabled={props.disabled}
        onChange={(_event: any, newValue: StructuredFormatting | null, reason: any) => {
          if (reason === 'clear') {
            onClear();
          } else {
            setAddress(newValue);
          }
          clearErrors();
        }}
        onClose={(_e: any, reason: any) => {
          if (reason !== 'selectOption') {
            if (inputValue && !freeSolo) {
              setError(name, {
                type: 'custom',
                message: intl.formatMessage({ id: 'POST_FORM.ERRORS.LOCATION_NOT_IN_THE_LIST' }),
              });
              setInputValue(null);
              onClear();
            } else {
              clearErrors();
            }
          }
        }}
        noOptionsText={intl.formatMessage({ id: 'POST_FORM.LOCALISATION_NO_RESULTS' })}
        onInputChange={(_e: any, newInputValue: any) => {
          setInputValue(newInputValue);
        }}
        getOptionLabel={(option: any) => option?.properties?.formatted || option}
        renderInput={(params: any) => (
          <TextField
            {...params}
            {...props}
            required={required && !geolocated}
            ref={ref}
            variant="outlined"
            placeholder={placeholder}
            label={
              props.hiddenLabel
                ? null
                : label || intl.formatMessage({ id: 'POSTS.FORM.ADDRESS.LABEL' })
            }
            slotProps={{
              input: {
                ...params.InputProps,
                ...(props.slotProps?.input || {}),
              },

              htmlInput: {
                'aria-label': label || intl.formatMessage({ id: 'POSTS.FORM.ADDRESS.LABEL' }),
                ...params.inputProps,
              },

              inputLabel: { shrink: true },
            }}
          />
        )}
      />
    );
  },
);

Address.displayName = 'Address';

export default Address;
